export enum EventPriority {
    UX = 1, // Lowest
    Warning = 2,
    Payment = 3, // Highest
}

export interface EventPayload {
    name: string;
    priority: EventPriority;
    timestamp: number;
    timeout?: number;
    /**
     * Optional arbitrary data associated with the event (e.g. scroll options).
     */
    data?: unknown;
}
export const DEFAULT_DELAY = 300;

class DynamicDebouncedAggregator {
    private buffer: EventPayload[] = [];
    private timer: number | null = null;
    private step = 0;

    constructor(
        private defaultDelay = DEFAULT_DELAY, // The fallback delay (e.g., 300ms)
        private handler: (event: EventPayload) => void,
        private scrollAfterStep = 0
    ) {
        this.processBuffer = this.processBuffer.bind(this);
    }

    /**
     * @param name Event name
     * @param priority Event priority
     * @param customTimeout (Optional) Specific delay for this event.
     * If provided, the debounce waits this long.
     */
    public enqueue(
        name: string,
        priority: EventPriority,
        customTimeout?: number,
        data?: unknown
    ): void {
        const newEvent: EventPayload = {
            name,
            priority,
            timestamp: Date.now(),
            timeout: customTimeout,
            data,
        };

        this.buffer.push(newEvent);

        if (this.timer) {
            clearTimeout(this.timer);
        }

        const delayToUse = customTimeout ?? this.defaultDelay;

        if (delayToUse <= 0) {
            this.processBuffer();
            return;
        }

        this.timer = window.setTimeout(this.processBuffer, delayToUse);
    }

    private processBuffer(): void {
        this.timer = null;
        if (this.buffer.length === 0) return;

        const winner = this.buffer.reduce((best, current) => {
            return current.priority >= best.priority ? current : best;
        }, this.buffer[0]);
        if (this.step > this.scrollAfterStep) {
            this.handler(winner);
        }
        this.buffer = [];
        this.step++;
    }

    /**
     * Cancel any pending timeout and clear buffered events.
     * Call this when the aggregator is no longer needed (e.g. on component unmount).
     */
    public dispose(): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        this.buffer = [];
    }
}

export default DynamicDebouncedAggregator;
